import React, { useState, useEffect, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useProductManagement } from "../ProductManagementWrapper";
import type { CategoryDto, ProductLightDto } from "../../../../domain/types/company/company-product-system";

interface SortableCategoryProps {
  category: CategoryDto;
  products: ProductLightDto[];
  onProductsReorder: (categoryId: number, products: ProductLightDto[]) => void;
}

const SortableCategory: React.FC<SortableCategoryProps> = ({
  category,
  products,
  onProductsReorder,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `category-${category.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleProductDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = Number(String(active.id).replace("product-", ""));
    const overId = Number(String(over.id).replace("product-", ""));

    const oldIndex = products.findIndex((p) => p.id === activeId);
    const newIndex = products.findIndex((p) => p.id === overId);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newProducts = arrayMove(products, oldIndex, newIndex);
      onProductsReorder(category.id, newProducts);
    }
  };

  const productSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex-shrink-0 w-[180px] bg-white rounded-xl border border-gray-200 overflow-hidden"
    >
      {/* 카테고리 헤더 (드래그 핸들) */}
      <div
        {...attributes}
        {...listeners}
        className="px-3 py-3 bg-gray-50 border-b border-gray-200 cursor-grab active:cursor-grabbing"
      >
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
          <span className="font-semibold text-sm text-gray-900 truncate">{category.name}</span>
        </div>
        <span className="text-xs text-gray-400 mt-1 block">{products.length}개 상품</span>
      </div>

      {/* 상품 리스트 */}
      <DndContext
        sensors={productSensors}
        collisionDetection={closestCenter}
        onDragEnd={handleProductDragEnd}
      >
        <SortableContext
          items={products.map((p) => `product-${p.id}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="p-2 space-y-1.5 max-h-[300px] overflow-y-auto">
            {products.map((product) => (
              <SortableProduct key={product.id} product={product} />
            ))}
            {products.length === 0 && (
              <div className="py-4 text-center text-xs text-gray-400">
                상품 없음
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

interface SortableProductProps {
  product: ProductLightDto;
}

const SortableProduct: React.FC<SortableProductProps> = ({ product }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `product-${product.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex items-center gap-2 px-2.5 py-2 bg-gray-50 rounded-lg cursor-grab active:cursor-grabbing hover:bg-gray-100 transition-colors"
    >
      <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
      </svg>
      <span className="text-xs text-gray-700 truncate">{product.name}</span>
    </div>
  );
};

export const CategoryProductSortManager: React.FC = () => {
  const {
    categories,
    bulkUpdateCategoryOrder,
    bulkUpdateProductOrder,
    refreshProducts,
  } = useProductManagement();

  const [localCategories, setLocalCategories] = useState<CategoryDto[]>([]);
  const [localProducts, setLocalProducts] = useState<Record<number, ProductLightDto[]>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeCategory, setActiveCategory] = useState<CategoryDto | null>(null);

  // 카테고리 데이터 초기화
  useEffect(() => {
    setLocalCategories([...categories]);
    const productsMap: Record<number, ProductLightDto[]> = {};
    categories.forEach((cat) => {
      productsMap[cat.id] = [...cat.products];
    });
    setLocalProducts(productsMap);
    setHasChanges(false);
  }, [categories]);

  const categorySensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleCategoryDragStart = (event: DragStartEvent) => {
    const categoryId = Number(String(event.active.id).replace("category-", ""));
    const category = localCategories.find((c) => c.id === categoryId);
    if (category) {
      setActiveCategory(category);
    }
  };

  const handleCategoryDragEnd = (event: DragEndEvent) => {
    setActiveCategory(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = Number(String(active.id).replace("category-", ""));
    const overId = Number(String(over.id).replace("category-", ""));

    const oldIndex = localCategories.findIndex((c) => c.id === activeId);
    const newIndex = localCategories.findIndex((c) => c.id === overId);

    if (oldIndex !== -1 && newIndex !== -1) {
      setLocalCategories(arrayMove(localCategories, oldIndex, newIndex));
      setHasChanges(true);
    }
  };

  const handleProductsReorder = useCallback((categoryId: number, products: ProductLightDto[]) => {
    setLocalProducts((prev) => ({
      ...prev,
      [categoryId]: products,
    }));
    setHasChanges(true);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // 카테고리 순서 저장
      const categoryOrders = localCategories.map((cat, index) => ({
        categoryId: cat.id,
        displayOrder: index,
      }));
      await bulkUpdateCategoryOrder(categoryOrders);

      // 상품 순서 저장 (모든 카테고리의 상품)
      const productOrders: Array<{ productId: number; displayOrder: number }> = [];
      Object.entries(localProducts).forEach(([, products]) => {
        products.forEach((product, index) => {
          productOrders.push({
            productId: product.id,
            displayOrder: index,
          });
        });
      });

      if (productOrders.length > 0) {
        await bulkUpdateProductOrder(productOrders);
      }

      setHasChanges(false);
      await refreshProducts();
    } catch (error) {
      console.error("순서 저장 실패:", error);
      alert("순서 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalCategories([...categories]);
    const productsMap: Record<number, ProductLightDto[]> = {};
    categories.forEach((cat) => {
      productsMap[cat.id] = [...cat.products];
    });
    setLocalProducts(productsMap);
    setHasChanges(false);
  };

  if (categories.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 mb-6">
      {/* 헤더 */}
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#4285F4]/10 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-[#4285F4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">카테고리 및 상품 순서 관리</h3>
            <p className="text-xs text-gray-500">드래그하여 순서를 변경하세요</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-xs text-[#4285F4] font-medium">변경사항 있음</span>
          )}
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* 콘텐츠 */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          <div className="p-4 overflow-x-auto">
            <DndContext
              sensors={categorySensors}
              collisionDetection={closestCenter}
              onDragStart={handleCategoryDragStart}
              onDragEnd={handleCategoryDragEnd}
            >
              <SortableContext
                items={localCategories.map((c) => `category-${c.id}`)}
                strategy={horizontalListSortingStrategy}
              >
                <div className="flex gap-4 pb-2">
                  {localCategories.map((category) => (
                    <SortableCategory
                      key={category.id}
                      category={category}
                      products={localProducts[category.id] || []}
                      onProductsReorder={handleProductsReorder}
                    />
                  ))}
                </div>
              </SortableContext>

              <DragOverlay>
                {activeCategory && (
                  <div className="w-[180px] bg-white rounded-xl border-2 border-[#4285F4] shadow-lg opacity-90">
                    <div className="px-3 py-3 bg-gray-50">
                      <span className="font-semibold text-sm text-gray-900">{activeCategory.name}</span>
                    </div>
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          </div>

          {/* 액션 버튼 */}
          {hasChanges && (
            <div className="flex justify-end gap-2 px-4 pb-4">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-[#222] rounded-lg hover:bg-[#333] disabled:opacity-50 transition-colors"
              >
                {saving ? "저장 중..." : "순서 저장"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryProductSortManager;
